import { Game, Tile } from './index'
import type { itemId, CompleteItemState, Position, Drop } from '../data/types'
import { calculateMergeDrops } from '../lib/merge-helpers'

// Get all spawnable item types for row 0 respawn
const spawnableItems: itemId[] = []

// Helper to initialize spawnable items list (called after game data is loaded)
function initSpawnableItems(game: Game) {
    if (spawnableItems.length === 0) {
        spawnableItems.push(
            ...Object.values(game.data.items)
                .filter(item => item.display)
                .map(item => item.id)
        )
    }
}

// Helper to get tiles within explosion radius (uses deltas wrapper)
function getTilesInExplosionRadius(game: Game, center: Position, radius: number): Position[] {
    const r = radius ?? 1
    return game.findPositionsFor(
        center,
        Infinity,  // get all positions in radius
        r,
        (pos) => !!game.getTileAt(pos)  // only valid tile positions
    )
}

// Helper to calculate activation drop positions for hover preview
function getActivationDrops(item: CompleteItemState, game: Game): Position[] {
    // 1. Shade exploders - no drops
    if (item.data.shade?.explodeOnShade) {
        return []
    }

    // 2. Check tap recipes exist
    const tapRecipes = item.data.tap?.recipes
    if (!tapRecipes) return []

    // 3. Find matching recipe (same logic as manual:activate)
    const recipe = [...tapRecipes].reverse().find(r =>
        r.charge === null || (item.charge !== undefined && item.charge >= r.charge)
    )
    if (!recipe) return []

    // 4. Priority: Exhaust drops > Charge-based drops
    let drops: Drop
    let dropRange: number | undefined

    if (recipe.exhaust !== null && item.data.exhaust) {
        // Exhaust-based
        const exhaustIndex = item.exhaustIndex ?? 0
        if (exhaustIndex >= item.data.exhaust.drops.length) return []
        drops = item.data.exhaust.drops[exhaustIndex]!
    } else {
        // Charge-based
        drops = recipe.drops
        const chargeRecipe = item.data.charge?.recipes.find(r => r.charge === recipe.charge)
        dropRange = chargeRecipe?.dropRange
    }

    // 5. Calculate drop count and positions
    const dropCount = Object.values(drops).reduce((a: number, b) => a + (b as number), 0)
    if (dropCount === 0) return []

    return game.findSpawnPositions(item.position!, dropCount, dropRange)
}

const SHADE_TICK_INTERVAL = 25

Game.registerHandlers({
    'tick'({ tick }) {
        // Update tick in state (UI already increments and passes it)
        this.state.tick = tick

        // Process all scheduled events at this tick
        this.executeScheduledEvents(this.state.tick);

        // Clear expired animations
        for (const item of this.state.items.values()) {
            if (item.animation && item.animation.endTick <= tick) {
                item.animation = undefined
            }
        }

        // Process charging for all items (skip if pausedInShade and under shade)
        for (const item of this.state.items.values()) {
            if (!item.data.charge || item.animation) continue

            // Skip charging if item is paused in shade
            if (item.data.shade?.pausedInShade) {
                const itemTile = item.position ? this.getTileAt(item.position) : null
                if (itemTile && itemTile.shade > 0) continue
            }

            // Initialize charge if undefined
            if (item.charge === undefined) {
                item.charge = 0
            }

            // Increment charge up to max
            if (item.charge < item.data.charge.max) {
                item.charge++
            }

            // Check for auto-spawn recipes (highest charge first)
            const recipes = [...item.data.charge.recipes].reverse()
            for (const recipe of recipes) {
                if (!recipe.autoSpawn) continue
                if (item.charge < recipe.charge) continue

                // Found a matching auto-spawn recipe
                const dropCount = Object.entries(recipe.drops)
                    .filter(([id]) => this.data.items[id as itemId])
                    .reduce((sum, [, count]) => sum + (count as number), 0)

                if (dropCount === 0) continue

                const positions = this.findSpawnPositions(
                    item.position!,
                    dropCount,
                    recipe.dropRange
                )

                if (positions.length >= dropCount) {
                    // Spawn and reset charge
                    const spawn: { itemId: itemId, position: Position }[] = []
                    let posIndex = 0
                    for (const [id, count] of Object.entries(recipe.drops)) {
                        if (!this.data.items[id as itemId]) continue
                        for (let i = 0; i < (count as number); i++) {
                            spawn.push({ itemId: id as itemId, position: positions[posIndex++]! })
                        }
                    }
                    this.spawnDrops(item.position!, spawn, recipe.triggerMerge ?? false)
                    item.charge -= recipe.charge
                }
                // else: no space, keep charge and wait
                break
            }
        }

        // Emit shade:tick every interval with rotating parity (0-3)
        if (tick % SHADE_TICK_INTERVAL === 0) {
            const parity = Math.floor(tick / SHADE_TICK_INTERVAL) % 4
            this.emit('shade:tick', { tick, parity })
        }

        // Check for shade contact (items with explodeOnShade on tiles with shade > 0)
        for (const item of this.state.items.values()) {
            if (!item.position || item.animation) continue
            if (!item.data.shade?.explodeOnShade) continue

            const tile = this.getTileAt(item.position)
            if (!tile || tile.shade <= 0) continue

            this.emit('shade:contact', { item, tile })
        }
    },

    'shade:tick'({ tick, parity }) {
        // Process ALL tiles, but each tile flows to only 1 neighbor direction based on parity
        // 0: flow RIGHT (x+1)
        // 1: flow DOWN (y+1)
        // 2: flow LEFT (x-1)
        // 3: flow UP (y-1)
        const flowDirections = [
            { dx: 1, dy: 0 },   // RIGHT
            { dx: 0, dy: 1 },   // DOWN
            { dx: -1, dy: 0 },  // LEFT
            { dx: 0, dy: -1 }   // UP
        ]
        const direction = flowDirections[parity]

        // Process shade spawning from ALL items with spawnShade
        for (const item of this.state.items.values()) {
            const spawnShade = item.data.shade?.spawnShade
            if (!spawnShade || !item.position || item.animation) continue

            const tile = this.getTileAt(item.position)
            if (!tile) continue

            // Only produce shade if covered by enough shade (or always if not enabledInShade)
            if (spawnShade.enabledInShade) {
                const minShade = spawnShade.minShade ?? 0
                if (tile.shade < minShade) continue
            }

            // Add shade to this tile (integer)
            tile.shade += Math.ceil(spawnShade.amount)
        }

        // Shade diffusion - viscous flow, only moves when diff >= 100
        // Process ALL tiles, but each flows to only ONE neighbor (based on parity)
        const shadeChanges = new Map<Tile, number>()

        for (const tile of this.state.tiles.values()) {
            if (tile.shade < 100) continue  // Can't flow if below 100

            // Get only the neighbor in the current direction
            const adjPos = { x: tile.position.x + direction.dx, y: tile.position.y + direction.dy }
            const adj = this.getTileAt(adjPos)
            if (!adj) continue

            const diff = tile.shade - adj.shade
            // Only flow if difference >= 100 (viscous)
            const flow = Math.floor(diff * 0.01)  // 1% of difference, floored
            if (flow >= 1) {
                shadeChanges.set(tile, (shadeChanges.get(tile) ?? 0) - flow)
                shadeChanges.set(adj, (shadeChanges.get(adj) ?? 0) + flow)
            }
        }

        // Apply shade changes (keep as integers)
        for (const [tile, change] of shadeChanges) {
            tile.shade = Math.max(0, tile.shade + change)
        }
    },

    'shade:contact'({ item, tile }) {
        const explode = item.data.shade?.explodeOnShade
        if (!explode) return

        this.emit('shade:explosion', {
            position: item.position!,
            power: explode.power,
            radius: explode.radius,
            item
        })
    },

    'drag:start'({ from }) {
        this.state.dragging = {
            from: from.position,
            over: null
        }
        this.state.highlight = null
    },

    'drag:over'({ over }) {
        if (!this.state.dragging) return

        this.state.dragging.over = over?.position ?? null

        const sourceTile = this.getTileAt(this.state.dragging.from)
        if (!sourceTile) {
            this.state.highlight = null
            return
        }
        const sourceItem = sourceTile.item
        if (!sourceItem) {
            this.state.highlight = null
            return
        }

        // Check for explosive preview when dragging over shaded empty tile
        const explode = sourceItem.data.shade?.explodeOnShade
        if (explode && over && !over.item && over.shade > 0) {
            // Show explosion radius
            const explosionTiles = getTilesInExplosionRadius(this, over.position, explode.radius ?? 1)
            const highlight: Record<string, 'explosion'> = {}

            for (const pos of explosionTiles) {
                const tile = this.getTileAt(pos)
                if (tile) {
                    highlight[tile.key] = 'explosion'  // Show explosion area in red
                }
            }

            this.state.highlight = highlight
            return
        }

        // Calculate highlights using flood fill from the over tile
        if (over?.item) {
            // If source item matches target item, highlight normally
            if (sourceItem.id === over.item.id) {
                const sourceKey = Tile.key(this.state.dragging.from)

                // Get connected tiles excluding the source tile (simulating drop from air)
                const connected = this.floodConnected(over, over.item.id, sourceTile)

                // Count: connected tiles (excluding source) + 1 (the dragged item being dropped)
                const totalCount = connected.length + 1

                // Check if this would result in a merge using calculateMergeDrops
                const itemData = over.item.data
                const mergeDrops = calculateMergeDrops(totalCount, itemData)
                const highlightType: 'merge' | 'match' = mergeDrops ? 'merge' : 'match'

                const highlight: Record<string, 'merge' | 'match'> = {}
                for (const tile of connected) {
                    highlight[tile.key] = highlightType
                }
                this.state.highlight = highlight
            } else {
                // Different item types - check if swapping would create a merge/match
                // Check adjacent tiles to the target position for matching source items
                const adjacent = this.getAdjacentTiles(over.position)
                const matchingAdjacent = adjacent.filter(t =>
                    t.item?.id === sourceItem.id && t.key !== sourceTile.key
                )

                if (matchingAdjacent.length > 0) {
                    // Simulate: dragged item would be placed at over.position
                    // Flood fill from adjacent matching tiles, treating over.position as if it has sourceItem
                    const connectedSet = new Set<Tile>()
                    for (const tile of matchingAdjacent) {
                        // Flood fill from this adjacent tile, excluding source tile
                        // Include tiles that would connect to over.position (simulating the swap)
                        const connected = this.floodConnected(tile, sourceItem.id, sourceTile)
                        for (const connectedTile of connected) {
                            connectedSet.add(connectedTile)
                        }
                    }

                    // The dragged item at over.position would connect with these tiles
                    // Count: connected tiles (excluding source) + 1 (the dragged item being placed)
                    const connected = Array.from(connectedSet)
                    const totalCount = connected.length + 1

                    // Check if this would result in a merge using calculateMergeDrops
                    const itemData = sourceItem.data
                    const mergeDrops = calculateMergeDrops(totalCount, itemData)
                    const highlightType: 'merge' | 'match' = mergeDrops ? 'merge' : 'match'

                    // Highlight the target tile and all connected matching tiles
                    const highlight: Record<string, 'merge' | 'match'> = {}
                    highlight[over.key] = highlightType
                    for (const tile of connected) {
                        highlight[tile.key] = highlightType
                    }
                    this.state.highlight = highlight
                } else {
                    // No merge/match, just show 'over' on target tile
                    this.state.highlight = { [over.key]: 'over' }
                }
            }
        } else if (over) {
            // Hovering over empty tile - check if adjacent tiles have matching items
            const adjacent = this.getAdjacentTiles(over.position)
            const matchingAdjacent = adjacent.filter(t =>
                t.item?.id === sourceItem.id && t.key !== Tile.key(this.state.dragging!.from)
            )

            if (matchingAdjacent.length > 0) {
                // Flood fill from all adjacent matching tiles to find all connected matching tiles
                const connectedSet = new Set<Tile>()
                for (const tile of matchingAdjacent) {
                    const connected = this.floodConnected(tile, sourceItem.id, sourceTile)
                    for (const connectedTile of connected) {
                        connectedSet.add(connectedTile)
                    }
                }
                const connected = Array.from(connectedSet)

                // Count: connected tiles (excluding source) + 1 (the dragged item being dropped)
                const totalCount = connected.length + 1

                // Check if this would result in a merge using calculateMergeDrops
                const itemData = sourceItem.data
                const mergeDrops = calculateMergeDrops(totalCount, itemData)
                const highlightType: 'merge' | 'match' = mergeDrops ? 'merge' : 'match'

                // Highlight the empty tile and all connected matching tiles
                const highlight: Record<string, 'merge' | 'match'> = {}
                highlight[over.key] = highlightType
                for (const tile of connected) {
                    highlight[tile.key] = highlightType
                }
                this.state.highlight = highlight
            } else {
                // No merge/match, just show 'over' on empty tile
                this.state.highlight = { [over.key]: 'over' }
            }
        } else {
            this.state.highlight = null
        }
    },

    'drag:end'() {
        this.state.dragging = null
        this.state.highlight = null
    },

    'manual:drop'({ from, to, item }) {
        if (from === to) return
        const itemOnTarget = to.item

        if (this.tryMerge(to, item, to.position)) {
            if (itemOnTarget) {
                if (!from) throw new Error('from is null')
                from.item = itemOnTarget
                itemOnTarget.position = from.position
                itemOnTarget.animation = {
                    type: 'merge:spread',
                    endTick: this.state.tick + this.data.animations['merge:spread'].duration,
                    from: to.position,
                    to: from.position
                }
            }
            to.item = item
            item.position = to.position
            return
        }

        if (!from) {
            return
        }

        if (to.shade > 0) {
            // Cannot swap with items on shaded tiles
            if (itemOnTarget) {
                return
            }
            // Check for item-on-shade attack (offensive explosion)
            const explode = item?.data.shade?.explodeOnShade
            if (item && explode) {
                // Remove item from source tile
                from.item = null

                // Trigger explosion at destination
                this.emit('shade:explosion', {
                    position: to.position,
                    power: explode.power,
                    radius: explode.radius,
                    item: item
                })
                return
            }
            return // Block all other shade drops
        }

        // Swap items
        from.item = null
        to.item = item
        item.position = to.position

        if (itemOnTarget) {
            from.item = itemOnTarget
            itemOnTarget.position = from.position
            itemOnTarget.animation = {
                type: 'merge:spread',
                endTick: this.state.tick + this.data.animations['merge:spread'].duration,
                from: to.position,
                to: from.position
            }
        }
    },

    'hover:activation'({ tile }) {
        // Don't show previews while dragging
        if (this.state.dragging) {
            this.state.highlight = null
            return
        }

        const item = tile.item
        if (!item || item.animation) {
            this.state.highlight = null
            return
        }

        // Items on shade cannot be activated
        if (tile.shade > 0) {
            this.state.highlight = null
            return
        }

        // Check if item is currently activatable
        const explode = item.data.shade?.explodeOnShade
        const isExploder = !!explode
        const dropPositions = getActivationDrops(item, this)
        const hasAvailableRecipe = dropPositions.length > 0

        // Only show highlight if exploder OR has an available recipe
        if (!isExploder && !hasAvailableRecipe) {
            this.state.highlight = null
            return
        }

        // Build highlight map
        const highlight: Record<string, 'merge' | 'match' | 'activation' | 'activation-drop' | 'explosion'> = {}
        highlight[tile.key] = 'activation'  // Yellow

        // Add explosion radius for exploders
        if (isExploder && item.position) {
            const explosionTiles = getTilesInExplosionRadius(this, item.position, explode!.radius ?? 1)
            for (const pos of explosionTiles) {
                const expTile = this.getTileAt(pos)
                if (expTile && expTile.key !== tile.key) {
                    highlight[expTile.key] = 'explosion'  // Show explosion area in red
                }
            }
        }

        // Add drop position highlights for regular items
        for (const pos of dropPositions) {
            const dropTile = this.getTileAt(pos)
            if (dropTile) {
                highlight[dropTile.key] = 'activation-drop'  // Light yellow
            }
        }

        this.state.highlight = highlight
    },

    'hover:end'() {
        // Only clear if it's an activation highlight (don't clear merge highlights)
        const firstHighlight = this.state.highlight && Object.values(this.state.highlight)[0]
        if (firstHighlight && (firstHighlight === 'activation' ||
            firstHighlight === 'activation-drop' ||
            firstHighlight === 'explosion')) {
            this.state.highlight = null
        }
    },

    'hover:item'({ tile, item }) {
        // Validate that the tile actually has an item
        if (!item || !tile.item) {
            throw new Error('hover:item called on tile without item')
        }

        // Set the hovered tile in state
        this.state.hoveredTile = tile
    },

    'hover:update'({ tile }) {
        // If dragging, update drag preview
        if (this.state.dragging) {
            const overTile = this.state.dragging.over ? this.getTileAt(this.state.dragging.over) : null
            this.emit('drag:over', { over: overTile })
            return
        }

        // Handle different tile states
        if (tile === 'out-of-grid') {
            // Mouse left the grid - clear everything
            this.state.hoveredTile = null
            this.emit('hover:end', {})
            return
        }

        if (tile === 'not-changed') {
            // Periodic refresh - update based on current hovered tile
            if (this.state.hoveredTile?.item) {
                this.emit('hover:activation', { tile: this.state.hoveredTile })
            } else {
                this.emit('hover:end', {})
            }
            return
        }

        // tile is an actual Tile object - update hover state
        if (tile.item) {
            // Hovering over item - will be set by hover:item event
            // Just refresh the activation preview if it's the current hovered tile
            if (this.state.hoveredTile?.key === tile.key) {
                this.emit('hover:activation', { tile })
            }
        } else {
            // Hovering over empty tile - clear hover
            this.state.hoveredTile = null
            this.emit('hover:end', {})
        }
    },

    'manual:activate'({ tile }) {
        const item = tile.item
        if (!item || item.animation) return

        // Items on shade cannot be activated
        if (tile.shade > 0) return

        // Check for tap-to-explode (items with explodeOnShade)
        const explode = item.data.shade?.explodeOnShade
        if (explode && item.position) {
            this.emit('shade:explosion', {
                position: item.position,
                power: explode.power,
                radius: explode.radius,
                item
            })
            return
        }

        const tapRecipes = item.data.tap?.recipes
        if (!tapRecipes) return

        // Find matching recipe (highest charge requirement that's met)
        const recipe = [...tapRecipes].reverse().find(r =>
            r.charge === null || (item.charge !== undefined && item.charge >= r.charge)
        )
        if (!recipe) return

        // Handle exhaust-based drops
        if (recipe.exhaust !== null && item.data.exhaust) {
            const exhaustIndex = item.exhaustIndex ?? 0
            const exhaustDrops = item.data.exhaust.drops

            if (exhaustIndex >= exhaustDrops.length) return // Already exhausted

            // Get current drop from exhaust array
            const currentDrop = exhaustDrops[exhaustIndex]!
            const dropCount = Object.values(currentDrop)
                .reduce((a: number, b) => a + (b as number), 0)

            if (dropCount > 0) {
                // Check if item will be exhausted after this tap
                const willBeExhausted = (exhaustIndex + 1) >= exhaustDrops.length
                const itemPosition = item.position!

                // If item will be exhausted, delete it first to free up its position
                if (willBeExhausted) {
                    this.state.items.delete(item.uid)
                    if (itemPosition) {
                        const itemTile = this.getTileAt(itemPosition)
                        if (itemTile?.item === item) {
                            itemTile.item = null
                        }
                    }
                }

                const positions = this.findSpawnPositions(itemPosition, dropCount)
                if (positions.length < dropCount) {
                    // No space - restore item if we deleted it
                    if (willBeExhausted) {
                        this.state.items.set(item.uid, item)
                        const itemTile = this.getTileAt(itemPosition)
                        if (itemTile) {
                            itemTile.item = item
                        }
                    }
                    return // Tap fails
                }

                // Spawn drops
                const spawnedItems: CompleteItemState[] = []
                let posIndex = 0
                for (const [itemId, count] of Object.entries(currentDrop)) {
                    for (let i = 0; i < (count as number) && posIndex < positions.length; i++) {
                        const pos = positions[posIndex++]!
                        const newItem = this.createItem(itemId as itemId, { position: pos })
                        spawnedItems.push(newItem)
                    }
                }

                // Schedule merge checks if enabled
                if (item.data.exhaust.triggerMerge && spawnedItems.length > 0) {
                    this.schedule('merge:check-chain', this.state.tick + 1, {
                        spawnedItemUids: spawnedItems.map(i => i.uid),
                        center: item.position!
                    })
                }

                // Update exhaust index if item wasn't already deleted
                if (!willBeExhausted) {
                    item.exhaustIndex = exhaustIndex + 1
                }
            } else {
                // No drops, just update exhaust index
                item.exhaustIndex = exhaustIndex + 1

                // Delete item if fully exhausted
                if (item.exhaustIndex >= exhaustDrops.length) {
                    this.state.items.delete(item.uid)
                    if (item.position) {
                        const itemTile = this.getTileAt(item.position)
                        if (itemTile?.item === item) {
                            itemTile.item = null
                        }
                    }
                }
            }
            return
        }

        // Handle regular recipe drops (charge-based)
        const dropCount = Object.entries(recipe.drops)
            .filter(([id]) => this.data.items[id as itemId])
            .reduce((sum, [, count]) => sum + (count as number), 0)

        if (dropCount > 0) {
            // Find positions (use charge recipe's dropRange if available, else unlimited)
            const chargeRecipe = item.data.charge?.recipes.find(r => r.charge === recipe.charge)
            const dropRange = chargeRecipe?.dropRange

            const positions = this.findSpawnPositions(item.position!, dropCount, dropRange)
            if (positions.length < dropCount) return // No space, tap fails

            // Spawn drops
            const spawn: { itemId: itemId, position: Position }[] = []
            let posIndex = 0
            for (const [id, count] of Object.entries(recipe.drops)) {
                if (!this.data.items[id as itemId]) continue
                for (let i = 0; i < (count as number); i++) {
                    spawn.push({ itemId: id as itemId, position: positions[posIndex++]! })
                }
            }
            this.spawnDrops(item.position!, spawn, chargeRecipe?.triggerMerge ?? false)
        }

        // Consume charge if required
        if (recipe.charge !== null) {
            item.charge! -= recipe.charge
        }
    },

    'merge:start'({ connectedTiles, connectedItems, center, drops }) {
        const endTick = this.state.tick + this.data.animations['merge:collapse'].duration

        // Animate all items merging to center (including floating items)
        for (const item of connectedItems) {
            if (item.position) {
                item.animation = {
                    type: 'merge:collapse',
                    endTick,
                    from: item.position,
                    to: center
                }
            }
        }

        // Schedule spawn event with serializable data
        this.schedule('merge:spawn', endTick, {
            itemUids: connectedItems.map(i => i.uid),
            center,
            drops
        })
    },

    'merge:spawn'({ items, center, drops }) {
        const endTick = this.state.tick + this.data.animations['merge:spread'].duration
        // Remove merged items (including floating ones)
        for (const item of items) {
            this.state.items.delete(item.uid)
            // Clear tile reference if item is on a tile
            if (item.position) {
                const tile = this.getTileAt(item.position)
                if (tile?.item === item) {
                    tile.item = null
                }
            }
        }

        // Calculate spawn count
        const totalDrops = Object.values(drops).reduce((a: number, b: unknown) => a + (b as number), 0)
        if (totalDrops === 0) return

        // Find spawn positions
        const spawnPositions = this.findSpawnPositions(center, totalDrops)
        if (spawnPositions.length === 0) return

        const spawnedItems: CompleteItemState[] = []
        let posIndex = 0

        // Spawn each item type
        for (const [itemId, count] of Object.entries(drops)) {
            for (let i = 0; i < (count as number) && posIndex < spawnPositions.length; i++) {
                const pos = spawnPositions[posIndex++]!
                const newItem = this.createItem(itemId, {
                    position: pos,
                    animation: {
                        type: 'merge:spread',
                        endTick,
                        from: center,
                        to: pos
                    }
                })
                spawnedItems.push(newItem)
            }
        }

        // Schedule complete event with serializable data
        if (spawnedItems.length > 0) {
            this.schedule('merge:complete', endTick, {
                spawnedItemUids: spawnedItems.map(i => i.uid),
                center
            })
        }
    },

    'merge:complete'({ spawnedItems, center }) {
        // Clear animations
        for (const item of spawnedItems) {
            item.animation = undefined
        }

        // Group spawned items by type
        const byType = new Map<string, typeof spawnedItems>()
        for (const item of spawnedItems) {
            const list = byType.get(item.id) ?? []
            list.push(item)
            byType.set(item.id, list)
        }
        this.emit('merge:check-chain', { spawnedItems, center })
    },

    'merge:check-chain'({ spawnedItems, center }) {
        // Check merge for each spawned item
        for (const item of spawnedItems) {
            if (!item.position) continue
            const tile = this.getTileAt(item.position)
            if (!tile || tile.item !== item) continue
            let merge = this.calculateDropMerge(tile, tile.item)
            if (!merge) continue
            if (merge.connectedTiles.find(t => t.key === Tile.key(center))) {
                this.tryMerge(tile, tile.item, center)
            } else {
                this.tryMerge(tile, tile.item)
            }
        }
    },

    // === SHADE EXPLOSION ===
    'shade:explosion'({ position, power, radius: explicitRadius, item }) {
        // Remove the exploding item from state and its tile
        this.state.items.delete(item.uid)

        // Clear from actual item position (may differ from explosion position in drop attacks)
        if (item.position) {
            const sourceTile = this.getTileAt(item.position)
            if (sourceTile?.item === item) {
                sourceTile.item = null
            }
        }

        // Also check explosion position (for contact explosions)
        const targetTile = this.getTileAt(position)
        if (targetTile?.item === item) {
            targetTile.item = null
        }

        // Calculate explosion radius (use explicit or derive from power)
        const radius = explicitRadius ?? Math.ceil(Math.sqrt(power))

        // Collect tiles in radius that have shade (use deltas wrapper)
        const shadedPositions = this.findPositionsFor(
            position,
            Infinity,  // get all positions in radius
            radius,
            (pos) => {
                const tile = this.getTileAt(pos)
                return !!tile && tile.shade > 0
            }
        )
        let affectedTiles: Tile[] = shadedPositions
            .map(pos => this.getTileAt(pos))
            .filter(Boolean) as Tile[]

        // Distribute damage evenly, redistribute leftover from killed tiles
        let remainingDamage = power
        while (remainingDamage > 0 && affectedTiles.length > 0) {
            // Split damage evenly across all affected tiles
            const damagePerTile = Math.floor(remainingDamage / affectedTiles.length)
            if (damagePerTile === 0) break  // Can't distribute any more

            let leftover = 0
            const stillAffected: Tile[] = []

            for (const tile of affectedTiles) {
                const actualDamage = Math.min(damagePerTile, tile.shade)
                tile.shade -= actualDamage
                leftover += damagePerTile - actualDamage  // Overkill goes to leftover

                if (tile.shade > 0) {
                    stillAffected.push(tile)
                }
            }

            // Continue with leftover damage on remaining tiles
            remainingDamage = leftover
            affectedTiles = stillAffected
        }
    }
})



// Register deserializers
Game.registerActionDeserializers({
    'merge:spawn'(data) {
        return {
            items: data.itemUids
                .map((uid) => this.state.items.get(uid))
                .filter(Boolean) as CompleteItemState[],
            center: data.center,
            drops: data.drops,
        }
    },

    'merge:complete'(data) {
        return {
            spawnedItems: data.spawnedItemUids
                .map((uid) => {
                    const item = this.state.items.get(uid)
                    if (!item) {
                        throw new Error(`Item with uid ${uid} not found`)
                    }
                    return item
                }),
            center: data.center
        }
    },

    'merge:check-chain'(data) {
        return {
            spawnedItems: data.spawnedItemUids
                .map((uid) => this.state.items.get(uid))
                .filter(Boolean) as CompleteItemState[],
            center: data.center
        }
    }
})


// Debug handlers
Game.registerHandlers({
    'debug:spawn'({ item, to }) {
        const endTick = this.state.tick + this.data.animations['merge:spread'].duration
        // Create item as if it was spawned (with spawning animation)
        const tile = this.getTileAt(to)
        if (!tile) {
            throw new Error(`Tile not found at position: ${to.x},${to.y}`)
        }
        if (tile.item) {
            throw new Error(`Tile at position ${to.x},${to.y} already has an item`)
        }

        // Create item with spawning animation from center
        const center = { x: Math.floor(this.state.size.x / 2), y: Math.floor(this.state.size.y / 2) }
        const newItem = this.createItem(item, {
            position: to,
            animation: {
                type: 'merge:spread',
                endTick,
                from: center,
                to: to
            }
        })
        this.emit('item:created', { item: newItem, position: to })
    },

    'debug:create-drop'({ item, to }) {
        // Create item as if it was dropped (no animation, just placed)
        const tile = this.getTileAt(to)
        if (!tile) {
            throw new Error(`Tile not found at position: ${to.x},${to.y}`)
        }
        if (tile.item) {
            throw new Error(`Tile at position ${to.x},${to.y} already has an item`)
        }

        const newItem = this.createItem(item, {
            position: to
        })
        this.emit('item:created', { item: newItem, position: to })
    },

    'debug:drag-drop'({ from, to }) {
        // Drag and drop item as if it was dragged
        const fromTile = this.getTileAt(from)
        const toTile = this.getTileAt(to)

        if (!fromTile) {
            throw new Error(`Tile not found at position: ${from.x},${from.y}`)
        }
        if (!toTile) {
            throw new Error(`Tile not found at position: ${to.x},${to.y}`)
        }
        if (!fromTile.item) {
            throw new Error(`No item at position: ${from.x},${from.y}`)
        }

        // Emit manual:drop to reuse existing logic
        this.emit('manual:drop', { from: fromTile, to: toTile, item: fromTile.item })
    },

    'debug:tap'({ at }) {
        // Check state and pass to regular event
        const tile = this.getTileAt(at)
        if (!tile) {
            throw new Error(`Tile not found at position: ${at.x},${at.y}`)
        }
        if (!tile.item) {
            throw new Error(`No item at position: ${at.x},${at.y}`)
        }

        // Pass to regular manual:activate event
        this.emit('manual:activate', { tile })
    }
})


// Debug deserializers
Game.registerActionDeserializers({
    'debug:spawn': e => e,
    'debug:create-drop': e => e,
    'debug:drag-drop': e => e,
    'debug:tap': e => e
})
